/* eslint-disable replo/no-tailwind-literal-color */
import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { Check, ChevronDown, ChevronRight, Copy } from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isCollapsible(
  value: unknown,
): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === "object";
}

function entryCount(value: Record<string, unknown> | unknown[]): number {
  return Array.isArray(value) ? value.length : Object.keys(value).length;
}

function bracketPair(value: unknown): [string, string] {
  return Array.isArray(value) ? ["[", "]"] : ["{", "}"];
}

const URL_REGEX = /^https?:\/\/\S+$/;
function isUrl(value: string): boolean {
  return URL_REGEX.test(value);
}

// ---------------------------------------------------------------------------
// Leaf value renderer – click to copy any value, ⌘+click URLs to open
// ---------------------------------------------------------------------------

const JsonValue: React.FC<{ value: unknown; isLast: boolean }> = ({
  value,
  isLast,
}) => {
  const [copied, setCopied] = React.useState(false);
  const isUrlValue = typeof value === "string" && isUrl(value);

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isUrlValue && e.metaKey) {
        window.open(value as string, "_blank");
        return;
      }
      const text = typeof value === "string" ? value : JSON.stringify(value);
      void navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    },
    [value, isUrlValue],
  );

  let tooltipContent = "Click to copy";
  if (copied) {
    tooltipContent = "Copied!";
  } else if (isUrlValue) {
    tooltipContent = "Click to copy · ⌘+click to open";
  }

  const comma = !isLast && <span className="shrink-0 text-slate-400">,</span>;

  const copyIcon = (
    <span className="ml-0.5 shrink-0 invisible group-hover/value:visible">
      {copied ? (
        <Check className="size-3 text-green-600" />
      ) : (
        <Copy className="size-3 text-slate-400" />
      )}
    </span>
  );

  if (value === null) {
    return (
      <Tooltip content={tooltipContent} triggerAsChild>
        <span
          className="group/value inline-flex min-w-0 cursor-pointer items-center hover:opacity-70"
          onClick={handleClick}
        >
          <span className={copied ? "text-green-600" : "text-slate-400"}>
            null
          </span>
          {comma}
          {copyIcon}
        </span>
      </Tooltip>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Tooltip content={tooltipContent} triggerAsChild>
        <span
          className="group/value inline-flex min-w-0 cursor-pointer items-center hover:opacity-70"
          onClick={handleClick}
        >
          <span className={copied ? "text-green-600" : "text-violet-600"}>
            {String(value)}
          </span>
          {comma}
          {copyIcon}
        </span>
      </Tooltip>
    );
  }

  if (typeof value === "number") {
    return (
      <Tooltip content={tooltipContent} triggerAsChild>
        <span
          className="group/value inline-flex min-w-0 cursor-pointer items-center hover:opacity-70"
          onClick={handleClick}
        >
          <span className={copied ? "text-green-600" : "text-amber-700"}>
            {value}
          </span>
          {comma}
          {copyIcon}
        </span>
      </Tooltip>
    );
  }

  if (typeof value === "string") {
    return (
      <Tooltip content={tooltipContent} triggerAsChild>
        <span
          className="group/value inline-flex min-w-0 cursor-pointer items-center hover:opacity-70"
          onClick={handleClick}
        >
          <span
            className={`min-w-0 truncate ${isUrlValue ? "hover:underline" : ""} ${copied ? "text-green-600" : "text-green-700"}`}
          >
            &quot;{value}&quot;
          </span>
          {comma}
          {copyIcon}
        </span>
      </Tooltip>
    );
  }

  // Fallback for undefined or other types
  return (
    <Tooltip content={tooltipContent} triggerAsChild>
      <span
        className="group/value inline-flex min-w-0 cursor-pointer items-center hover:opacity-70"
        onClick={handleClick}
      >
        <span className={copied ? "text-green-600" : "text-slate-400"}>
          {String(value)}
        </span>
        {copyIcon}
      </span>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Recursive tree node (depth-aware)
// ---------------------------------------------------------------------------

const JsonNode: React.FC<{
  keyName?: string;
  value: unknown;
  isLast: boolean;
  defaultExpandDepth: number;
  currentDepth: number;
}> = ({ keyName, value, isLast, defaultExpandDepth, currentDepth }) => {
  const shouldDefaultExpand = currentDepth < defaultExpandDepth;
  const [expanded, setExpanded] = React.useState(shouldDefaultExpand);

  // Leaf values (non-object)
  if (!isCollapsible(value)) {
    return (
      <div className="flex min-w-0 items-start leading-5">
        {keyName !== undefined && (
          <>
            <span className="shrink-0 text-blue-700">
              &quot;{keyName}&quot;
            </span>
            <span className="shrink-0 text-slate-500">:&nbsp;</span>
          </>
        )}
        <JsonValue value={value} isLast={isLast} />
      </div>
    );
  }

  // Collapsible node (object / array)
  const entries = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  const [open, close] = bracketPair(value);
  const count = entryCount(value);

  return (
    <div className="leading-5">
      {/* Toggle row */}
      <button
        type="button"
        className="inline-flex cursor-pointer items-center gap-0 text-left hover:bg-slate-100"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? (
          <ChevronDown className="size-3 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="size-3 shrink-0 text-slate-400" />
        )}
        {keyName !== undefined && (
          <>
            <span className="text-blue-700">&quot;{keyName}&quot;</span>
            <span className="text-slate-500">:&nbsp;</span>
          </>
        )}
        <span className="text-slate-500">{open}</span>
        {!expanded && (
          <>
            <span className="px-0.5 text-slate-400">
              {count} {count === 1 ? "item" : "items"}
            </span>
            <span className="text-slate-500">{close}</span>
            {!isLast && <span className="text-slate-400">,</span>}
          </>
        )}
      </button>

      {/* Children */}
      {expanded && (
        <>
          <div className="ml-4 border-l border-slate-200 pl-2">
            {entries.map(([childKey, childValue], idx) => (
              <JsonNode
                key={childKey}
                keyName={Array.isArray(value) ? undefined : childKey}
                value={childValue}
                isLast={idx === entries.length - 1}
                defaultExpandDepth={defaultExpandDepth}
                currentDepth={currentDepth + 1}
              />
            ))}
            {entries.length === 0 && (
              <span className="text-slate-400 italic">empty</span>
            )}
          </div>
          <div>
            <span className="text-slate-500">{close}</span>
            {!isLast && <span className="text-slate-400">,</span>}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * Renders a JSON value as a collapsible tree. Objects and arrays can be
 * toggled open/closed. Clicking any leaf value copies it to the clipboard.
 *
 * `defaultExpandDepth` controls how many levels deep nodes are expanded by
 * default (1 = root children visible, deeper levels collapsed).
 */
const CollapsibleJsonTree: React.FC<{
  data: unknown;
  defaultExpandDepth?: number;
}> = ({ data, defaultExpandDepth = 1 }) => {
  return (
    <div className="overflow-auto rounded bg-slate-50 p-2 font-mono text-xs text-slate-700">
      <JsonNode
        value={data}
        isLast={true}
        defaultExpandDepth={defaultExpandDepth}
        currentDepth={0}
      />
    </div>
  );
};

export { CollapsibleJsonTree };
